import { useEffect } from 'react';
import { getApplication } from '../../../../../../javascript/models/Application';

export type MergeCompletionData = {
  bulkRequestId?: number;
  archivedTaskIds?: number[];
  newTaskId?: number;
};

/**
 * Listens for task_merge completion (via window callback, custom event, or polling)
 * and invokes onMergeComplete when the merge for the current merging bulk request
 * has finished.
 */
export const useMergeCompletion = (
  merging: number | boolean,
  onMergeComplete: (data: MergeCompletionData) => void
) => {
  useEffect(() => {
    if (!merging) return;

    const processedBulkRequestIds = new Set<number>();

    const applyMergeComplete = (data: MergeCompletionData) => {
      if (!data || data.bulkRequestId == null) return;
      if (Number(data.bulkRequestId) !== Number(merging)) return;
      if (processedBulkRequestIds.has(Number(data.bulkRequestId))) return;
      processedBulkRequestIds.add(Number(data.bulkRequestId));

      const app = getApplication() as { set?: (k: string, v: unknown) => void };
      app?.set?.('merging_bulk_request_id', null);
      app?.set?.('merge_completed', null);
      onMergeComplete(data);
    };

    const eventHandler = (e: Event) =>
      applyMergeComplete((e as CustomEvent).detail);

    window.addEventListener('bugherd:merge_completed', eventHandler);

    const pollInterval = setInterval(() => {
      const app = getApplication() as { get?: (k: string) => unknown };
      const data = app?.get?.('merge_completed') as MergeCompletionData | undefined;
      if (data) applyMergeComplete(data);
    }, 500);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('bugherd:merge_completed', eventHandler);
    };
  }, [merging, onMergeComplete]);
};
